import React, {useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {Notify, Loading} from 'notiflix';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  FormLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AddCircleOutline,
  ArrowBack,
  CheckCircle,
  Delete as DeleteIcon,
  DragIndicator,
  Save,
} from '@mui/icons-material';
import MDBox from 'components/MDBox';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import {understandItBetterApi} from 'api/understandItBetterApi';

const ENTITY_TYPES = [
  {value: 'product', label: 'Product', color: '#1565c0', bg: '#e3f2fd'},
  {value: 'valuepack', label: 'Value Pack', color: '#7b1fa2', bg: '#f3e5f5'},
//   {
//     value: 'question_answer',
//     label: 'Question & Answer',
//     color: '#2e7d32',
//     bg: '#e8f5e9',
//   },
];

const normalizeEntityType = type => {
  if (!type) return '';
  const map = {
    products: 'product',
    product: 'product',
    valuepacks: 'valuepack',
    valuepack: 'valuepack',
    'value-packs': 'valuepack',
    plans: 'question_answer',
    question_answer: 'question_answer',
    question_answers: 'question_answer',
  };
  return map[type.toLowerCase()] || type.toLowerCase();
};

const getApiEntityType = type => {
  const map = {
    product: 'products',
    valuepack: 'valuepacks',
    question_answer: 'plans',
  };
  return map[type] || type;
};

const emptySection = (
  isNew = true,
  dbId = null,
  title = '',
  description = '',
) => ({
  _localId: Date.now() + Math.random(),
  isNew,
  dbId,
  title,
  description,
});

const UnderstandItBetterCreate = () => {
  const navigate = useNavigate();
  const {id} = useParams();
  const isEditMode = !!id;

  const [step, setStep] = useState(0);
  const [entityType, setEntityType] = useState('');
  const [entityId, setEntityId] = useState('');
  const [pendingEntityId, setPendingEntityId] = useState(null);
  const [entityList, setEntityList] = useState([]);
  const [entityListLoading, setEntityListLoading] = useState(false);
  const [sections, setSections] = useState([emptySection()]);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState({});
  const [pageLoading, setPageLoading] = useState(isEditMode);

  useEffect(() => {
    if (isEditMode && id) {
      fetchExistingSection();
    }
  }, [isEditMode, id]);

  const fetchExistingSection = async () => {
    try {
      setPageLoading(true);

      const data = await understandItBetterApi.getById(id);
      setEntityType(normalizeEntityType(data.entityType));
      setPendingEntityId(data.entityId || '');

      setSections([
        emptySection(false, data.id, data.title || '', data.description || ''),
      ]);

      setStep(1);
    } catch (err) {
      Notify.failure('Failed to load section data');
      navigate('/admin/understand-it-better');
    } finally {
      setPageLoading(false);
    }
  };

  useEffect(() => {
    if (!entityType) {
      setEntityList([]);
      if (!isEditMode) setEntityId('');
      return;
    }
    fetchEntityList(entityType);
  }, [entityType]);

  const fetchEntityList = async type => {
    try {
      setEntityListLoading(true);
      if (!isEditMode) setEntityId('');
      setEntityList([]);

      let data = [];
      if (type === 'product') {
        data = await understandItBetterApi.getProductList();
      } else if (type === 'valuepack') {
        data = await understandItBetterApi.getValuePackList();
      } else if (type === 'question_answer') {
        data = await understandItBetterApi.getQAList();
      }

      const list = Array.isArray(data) ? data : [];
      setEntityList(list);

      if (pendingEntityId) {
        setEntityId(pendingEntityId);
        setPendingEntityId(null);
      }
    } catch (err) {
      console.error('Error fetching entity list:', err);
      Notify.failure('Failed to load entity list');
    } finally {
      setEntityListLoading(false);
    }
  };

  const addSection = () => setSections(prev => [...prev, emptySection(true)]);

  const removeSection = async localId => {
    if (sections.length === 1) {
      Notify.warning('At least one section is required');
      return;
    }

    const section = sections.find(s => s._localId === localId);

    if (isEditMode && section?.dbId) {
      Loading.circle('Removing section...');
      try {
        await understandItBetterApi.deleteOne(section.dbId);
        Notify.success('Section removed');
      } catch (err) {
        Loading.remove();
        Notify.failure(err.message || 'Failed to remove section');
        return;
      } finally {
        Loading.remove();
      }
    }

    setSections(prev => prev.filter(s => s._localId !== localId));
  };

  const updateSection = (localId, field, value) => {
    setSections(prev =>
      prev.map(s => (s._localId === localId ? {...s, [field]: value} : s)),
    );
    setErrors(prev => {
      const updated = {...prev};
      delete updated[`${localId}_${field}`];
      return updated;
    });
  };

  const validateStep0 = () => {
    const newErrors = {};
    if (!entityType) newErrors.entityType = 'Please select an entity type';
    if (!entityId) newErrors.entityId = 'Please select an entity';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep1 = () => {
    const newErrors = {};
    sections.forEach(s => {
      if (!s.title.trim()) newErrors[`${s._localId}_title`] = 'Title is required';
      if (!s.description.trim()) {
        newErrors[`${s._localId}_description`] = 'Description is required';
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleProceedToSections = () => {
    if (validateStep0()) setStep(1);
  };

  const handleSubmit = async () => {
    if (!validateStep1()) {
      Notify.failure('Please fill in all required fields');
      return;
    }

    try {
      setSubmitting(true);
      Loading.circle('Saving...');

      if (isEditMode) {
        await Promise.all(
          sections.map(s =>
            s.isNew
              ? understandItBetterApi.createOne({
                  entityId,
                  entityType: getApiEntityType(entityType),
                  title: s.title,
                  description: s.description,
                })
              : understandItBetterApi.updateOne(s.dbId, {
                  title: s.title,
                  description: s.description,
                }),
          ),
        );
      } else {
        await Promise.all(
          sections.map(s =>
            understandItBetterApi.createOne({
              entityId,
              entityType: getApiEntityType(entityType),
              title: s.title,
              description: s.description,
            }),
          ),
        );
      }

      setSuccess(true);
      setTimeout(() => navigate('/admin/understand-it-better'), 1500);
    } catch (err) {
      Notify.failure(err.message || 'Failed to save sections');
    } finally {
      Loading.remove();
      setSubmitting(false);
    }
  };

  const selectedEntityTypeObj = ENTITY_TYPES.find(t => t.value === entityType);
  const selectedEntityObj = entityList.find(e => e.id === entityId);
  const selectedEntityName =
    selectedEntityObj?.name || selectedEntityObj?.title || entityId;

  const newSectionCount = sections.filter(s => s.isNew).length;
  const existingSectionCount = sections.filter(s => !s.isNew).length;

  if (success) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="60vh">
          <Card
            sx={{
              p: 6,
              textAlign: 'center',
              maxWidth: 420,
              border: '2px solid #86efac',
              bgcolor: '#f0fdf4',
            }}>
            <CheckCircle sx={{fontSize: 80, color: '#15803d', mb: 2}} />
            <Typography
              variant="h4"
              sx={{color: '#15803d', fontWeight: 700, mb: 1}}>
              {isEditMode ? 'Changes Saved' : 'Sections Created'}
            </Typography>
            <Typography variant="body1" color="textSecondary">
              {isEditMode
                ? `${existingSectionCount} section(s) updated, ${newSectionCount} new section(s) added.`
                : `${sections.length} section(s) created successfully.`}
            </Typography>
            <Typography variant="body2" color="textSecondary" mt={1}>
              Redirecting to list...
            </Typography>
          </Card>
        </Box>
        <Footer />
      </DashboardLayout>
    );
  }

  if (pageLoading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="60vh"
          flexDirection="column">
          <CircularProgress />
          <Typography variant="h6" mt={2}>
            Loading section data...
          </Typography>
        </Box>
        <Footer />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Box mx="auto" px={2}>
          <Box display="flex" alignItems="center" mb={4}>
            <Button
              startIcon={<ArrowBack />}
              onClick={() => navigate('/admin/understand-it-better')}
              sx={{mr: 2}}>
              Back
            </Button>
            <Typography variant="h4" fontWeight={700}>
              {isEditMode ? 'Edit Section' : 'Create Understand It Better'}
            </Typography>
          </Box>

          {isEditMode && (
            <Alert severity="info" sx={{mb: 3}}>
              <strong>Edit Mode:</strong> You are editing an existing section.
              You can also add new sections for this entity.
            </Alert>
          )}

          {!isEditMode && (
            <Card
              sx={{
                p: 2,
                mb: 3,
                bgcolor: '#f8fafc',
                border: '1px solid #e2e8f0',
              }}>
              <Box display="flex" alignItems="center" gap={2}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      bgcolor: step >= 0 ? '#1976d2' : '#e2e8f0',
                      color: step >= 0 ? '#fff' : '#64748b',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 700,
                      fontSize: 14,
                    }}>
                    {step > 0 ? '✓' : '1'}
                  </Box>
                  <Typography
                    variant="body2"
                    fontWeight={step === 0 ? 700 : 500}
                    color={
                      step === 0 ? '#1976d2' : step > 0 ? '#15803d' : '#64748b'
                    }>
                    Select Entity
                  </Typography>
                </Box>

                <Box
                  sx={{
                    flex: 1,
                    height: 2,
                    bgcolor: step > 0 ? '#1976d2' : '#e2e8f0',
                    borderRadius: 1,
                  }}
                />

                <Box display="flex" alignItems="center" gap={1}>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      bgcolor: step >= 1 ? '#1976d2' : '#e2e8f0',
                      color: step >= 1 ? '#fff' : '#64748b',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 700,
                      fontSize: 14,
                    }}>
                    2
                  </Box>
                  <Typography
                    variant="body2"
                    fontWeight={step === 1 ? 700 : 500}
                    color={step === 1 ? '#1976d2' : '#64748b'}>
                    Add Sections
                  </Typography>
                </Box>
              </Box>
            </Card>
          )}

          {(step === 0 || isEditMode) && (
            <Card sx={{mb: 3}}>
              <CardContent sx={{p: 3}}>
                <Typography
                  variant="h6"
                  fontWeight={700}
                  mb={0.5}
                  color="#1e3a5f">
                  {isEditMode ? 'Entity Details' : 'Step 1: Select Entity'}
                </Typography>
                <Typography variant="body2" color="textSecondary" mb={3}>
                  {isEditMode
                    ? 'The entity this section belongs to'
                    : 'Choose the entity type and the specific entity for these sections'}
                </Typography>

                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <FormLabel
                      sx={{
                        fontWeight: 600,
                        color: '#1e3a5f',
                        mb: 1,
                        display: 'block',
                      }}>
                      Entity Type *
                    </FormLabel>

                    <Box display="flex" flexDirection="column" gap={1.5}>
                      {ENTITY_TYPES.map(type => {
                        const isSelected = entityType === type.value;
                        return (
                          <Card
                            key={type.value}
                            onClick={() =>
                              !isEditMode && setEntityType(type.value)
                            }
                            sx={{
                              border: isSelected
                                ? `2px solid ${type.color}`
                                : '2px solid #e5e7eb',
                              bgcolor: isSelected ? type.bg : '#fff',
                              cursor: isEditMode ? 'default' : 'pointer',
                              p: 1.5,
                              transition: 'all 0.2s ease',
                              '&:hover': isEditMode
                                ? {}
                                : {
                                    borderColor: type.color,
                                    transform: 'translateY(-1px)',
                                    boxShadow: `0 4px 12px ${type.color}25`,
                                  },
                            }}>
                            <Box display="flex" alignItems="center" gap={1.5}>
                              <Box
                                sx={{
                                  width: 20,
                                  height: 20,
                                  borderRadius: '50%',
                                  border: `2px solid ${
                                    isSelected ? type.color : '#94a3b8'
                                  }`,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  flexShrink: 0,
                                }}>
                                {isSelected && (
                                  <Box
                                    sx={{
                                      width: 10,
                                      height: 10,
                                      borderRadius: '50%',
                                      bgcolor: type.color,
                                    }}
                                  />
                                )}
                              </Box>
                              <Typography
                                variant="body2"
                                fontWeight={600}
                                color={isSelected ? type.color : '#475569'}>
                                {type.label}
                              </Typography>
                              {isSelected && (
                                <Chip
                                  label="Selected"
                                  size="small"
                                  sx={{
                                    ml: 'auto',
                                    bgcolor: type.bg,
                                    color: type.color,
                                    fontWeight: 600,
                                    fontSize: '0.7rem',
                                  }}
                                />
                              )}
                            </Box>
                          </Card>
                        );
                      })}
                    </Box>

                    {errors.entityType && (
                      <Typography
                        variant="caption"
                        color="error"
                        mt={0.5}
                        display="block">
                        {errors.entityType}
                      </Typography>
                    )}
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormLabel
                      sx={{
                        fontWeight: 600,
                        color: '#1e3a5f',
                        mb: 1,
                        display: 'block',
                      }}>
                      Select {selectedEntityTypeObj?.label || 'Entity'} *
                    </FormLabel>

                    {!entityType ? (
                      <Box
                        sx={{
                          p: 3,
                          border: '2px dashed #e2e8f0',
                          borderRadius: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minHeight: 120,
                          color: '#94a3b8',
                        }}>
                        <Typography variant="body2" textAlign="center">
                          Please select an entity type first
                        </Typography>
                      </Box>
                    ) : entityListLoading ? (
                      <Box display="flex" justifyContent="center" py={4}>
                        <CircularProgress size={28} />
                      </Box>
                    ) : (
                      <FormControl fullWidth error={!!errors.entityId}>
                        <InputLabel>
                          Select {selectedEntityTypeObj?.label} *
                        </InputLabel>
                        <Select
                          value={entityId}
                          onChange={e => setEntityId(e.target.value)}
                          label={`Select ${selectedEntityTypeObj?.label} *`}
                          disabled={isEditMode}>
                          <MenuItem value="">
                            <em>-- Select an option --</em>
                          </MenuItem>
                          {entityList.map(entity => (
                            <MenuItem key={entity.id} value={entity.id}>
                              {entity.name || entity.title || entity.id}
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.entityId && (
                          <Typography
                            variant="caption"
                            color="error"
                            mt={0.5}
                            display="block">
                            {errors.entityId}
                          </Typography>
                        )}
                      </FormControl>
                    )}

                    {entityId && selectedEntityObj && (
                      <Alert
                        severity="success"
                        sx={{
                          mt: 2,
                          bgcolor: '#f0fdf4',
                          border: '1px solid #86efac',
                        }}
                        icon={false}>
                        <Typography
                          variant="caption"
                          fontWeight={600}
                          color="#15803d">
                          Selected: {selectedEntityName}
                        </Typography>
                      </Alert>
                    )}
                  </Grid>
                </Grid>

                {!isEditMode && step === 0 && (
                  <Box display="flex" justifyContent="flex-end" mt={3}>
                    <Button
                      variant="contained"
                      onClick={handleProceedToSections}
                      disabled={!entityType || !entityId}
                      sx={{
                        bgcolor: '#1976d2',
                        color: '#fff',
                        px: 4,
                        '&:hover': {bgcolor: '#1565c0'},
                        '&:disabled': {bgcolor: '#e2e8f0'},
                      }}>
                      Next: Add Sections →
                    </Button>
                  </Box>
                )}
              </CardContent>
            </Card>
          )}

          {step === 1 && (
            <>
              <Card
                sx={{
                  p: 2,
                  mb: 3,
                  bgcolor: selectedEntityTypeObj?.bg || '#f0f9ff',
                  border: `1px solid ${
                    selectedEntityTypeObj?.color || '#bae6fd'
                  }40`,
                }}>
                <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                  <Chip
                    label={selectedEntityTypeObj?.label || entityType}
                    size="small"
                    sx={{
                      bgcolor: selectedEntityTypeObj?.bg,
                      color: selectedEntityTypeObj?.color,
                      fontWeight: 700,
                    }}
                  />
                  <Typography variant="body2" fontWeight={600} color="#1e3a5f">
                    {selectedEntityName || entityId}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {isEditMode
                      ? `${existingSectionCount} existing · ${newSectionCount} new`
                      : `- ${sections.length} section(s) to be saved`}
                  </Typography>
                  {!isEditMode && (
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => setStep(0)}
                      sx={{ml: 'auto', color: '#64748b'}}>
                      ← Change Entity
                    </Button>
                  )}
                </Box>
              </Card>

              <Box display="flex" flexDirection="column" gap={2} mb={3}>
                {sections.map((section, index) => (
                  <Card
                    key={section._localId}
                    sx={{
                      border: section.isNew
                        ? '1px solid #86efac'
                        : '1px solid #e2e8f0',
                      transition: 'box-shadow 0.2s',
                      '&:hover': {boxShadow: '0 4px 16px rgba(0,0,0,0.08)'},
                    }}>
                    <CardContent sx={{p: 3}}>
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        mb={2.5}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <DragIndicator sx={{color: '#94a3b8', fontSize: 20}} />
                          <Typography
                            variant="subtitle1"
                            fontWeight={700}
                            color="#1e3a5f">
                            Section {index + 1}
                          </Typography>
                          {isEditMode && (
                            <Chip
                              label={section.isNew ? 'New' : 'Existing'}
                              size="small"
                              sx={{
                                bgcolor: section.isNew ? '#dcfce7' : '#e0f2fe',
                                color: section.isNew ? '#15803d' : '#0369a1',
                                fontWeight: 600,
                                fontSize: '0.65rem',
                              }}
                            />
                          )}
                          {section.title && (
                            <Chip
                              label={
                                section.title.length > 30
                                  ? `${section.title.slice(0, 30)}...`
                                  : section.title
                              }
                              size="small"
                              sx={{
                                bgcolor: '#f1f5f9',
                                color: '#475569',
                                fontSize: '0.7rem',
                              }}
                            />
                          )}
                        </Box>
                        <Tooltip title="Remove section">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => removeSection(section._localId)}
                              disabled={sections.length === 1}
                              sx={{
                                color: '#ef4444',
                                '&:hover': {bgcolor: '#fef2f2'},
                                '&:disabled': {color: '#cbd5e1'},
                              }}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>

                      <Grid container spacing={2.5}>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Title *"
                            value={section.title}
                            onChange={e =>
                              updateSection(
                                section._localId,
                                'title',
                                e.target.value,
                              )
                            }
                            error={!!errors[`${section._localId}_title`]}
                            helperText={
                              errors[`${section._localId}_title`] ||
                              `${section.title.length}/120 characters`
                            }
                            inputProps={{maxLength: 120}}
                            placeholder="e.g. What makes this product unique?"
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            multiline
                            rows={3}
                            label="Description *"
                            value={section.description}
                            onChange={e =>
                              updateSection(
                                section._localId,
                                'description',
                                e.target.value,
                              )
                            }
                            error={!!errors[`${section._localId}_description`]}
                            helperText={
                              errors[`${section._localId}_description`] ||
                              `${section.description.length} characters`
                            }
                            placeholder="Enter a detailed description..."
                          />
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                ))}
              </Box>

              <Button
                fullWidth
                variant="outlined"
                onClick={addSection}
                startIcon={<AddCircleOutline />}
                sx={{
                  mb: 3,
                  py: 1.5,
                  borderStyle: 'dashed',
                  borderColor: '#1976d2',
                  color: '#1976d2',
                  bgcolor: '#f0f9ff',
                  '&:hover': {bgcolor: '#dbeafe', borderStyle: 'dashed'},
                }}>
                Add Another Section
              </Button>

              <Card sx={{p: 2.5, bgcolor: '#f8fafc', border: '1px solid #e2e8f0'}}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  flexWrap="wrap"
                  gap={2}>
                  <Typography variant="body2" color="textSecondary">
                    {isEditMode
                      ? `${existingSectionCount} section(s) will be updated · ${newSectionCount} new section(s) will be created`
                      : `${sections.length} section(s) ready to be saved`}
                  </Typography>
                  <Box display="flex" gap={2}>
                    <Button
                      variant="outlined"
                      onClick={() => navigate('/admin/understand-it-better')}
                      disabled={submitting}
                      sx={{
                        color: 'grey.600',
                        borderColor: 'grey.400',
                        '&:hover': {
                          borderColor: 'grey.600',
                          backgroundColor: 'grey.100',
                        },
                      }}>
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleSubmit}
                      disabled={submitting}
                      startIcon={
                        submitting ? (
                          <CircularProgress size={18} color="inherit" />
                        ) : (
                          <Save />
                        )
                      }
                      sx={{
                        bgcolor: isEditMode ? '#d97706' : '#1976d2',
                        color: '#fff',
                        px: 4,
                        '&:hover': {
                          bgcolor: isEditMode ? '#b45309' : '#1565c0',
                        },
                      }}>
                      {submitting
                        ? 'Saving...'
                        : isEditMode
                        ? `Save Changes (${sections.length})`
                        : `Save ${sections.length} Section(s)`}
                    </Button>
                  </Box>
                </Box>
              </Card>
            </>
          )}
        </Box>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
};

export default UnderstandItBetterCreate;
